import {
  isResolvedCitation,
  splitCitationSegments,
} from 'app/pages/loanApplication/components/GuidelineCitations/citationMarkers.utils';
import _ from 'lodash';

import { AiProcessStepTerminalStatuses, LoadStatus } from 'constants/aiProcessRuns';

const PENDING_STATUSES = new Set([LoadStatus.IDLE, LoadStatus.LOADING]);

// How a citation chip / card renders: resolved (full chip), pending (a source that can
// still resolve it is loading) or unavailable (every source settled without it).
export const resolveCitationDisplayState = (citation, status) => {
  if (isResolvedCitation(citation)) {
    return { isResolved: true, isPending: false, isUnavailable: false };
  }

  const isPending = PENDING_STATUSES.has(status);

  return { isResolved: false, isPending, isUnavailable: !isPending };
};

// Combines the per-step and run-wide citation load states into one: pending while any
// relevant source is still loading, otherwise settled.
export const combineCitationStatuses = statuses => {
  const relevant = statuses.filter(status => !_.isNil(status));

  if (relevant.some(status => PENDING_STATUSES.has(status))) {
    return LoadStatus.LOADING;
  }

  return relevant.every(status => status === LoadStatus.ERROR) && relevant.length > 0
    ? LoadStatus.ERROR
    : LoadStatus.READY;
};

const collectMarkerKeys = text =>
  splitCitationSegments(text)
    .filter(segment => segment.type === 'citation')
    .map(segment => segment.key.toLowerCase());

// A run's citation list only needs re-fetching when new markers can have appeared: a
// review-form value introduced a new citation key, or another step finished (its
// output / citation record is now persisted). The signature captures exactly that, so
// a live run refreshes as it fills without re-fetching on every unrelated update.
export const buildCitationRefreshSignature = ({ form, steps }) => {
  const formKeys = (form?.fields ?? []).flatMap(field => [
    ...collectMarkerKeys(field?.value),
    ...collectMarkerKeys(field?.overriddenValue),
  ]);
  const terminalStepCount = (steps ?? []).filter(step =>
    AiProcessStepTerminalStatuses.has(step?.status),
  ).length;

  return `${terminalStepCount}|${_.uniq(formKeys).sort().join(',')}`;
};

// Ordered render segments with stable keys for <CitedText>.
export const buildCitedTextSegments = text =>
  splitCitationSegments(text).map((segment, idx) => ({
    ...segment,
    renderKey: `${segment.type}-${idx}-${segment.type === 'citation' ? segment.key : segment.value.length}`,
  }));
